/**
 * Last-used sign-in method — the device-local hint the auth pages paint on
 * first render (a "Last used" badge + highlighted border on that method).
 *
 * Two sources, no network, no enumeration:
 *  1. The Supabase session in localStorage (`sb-*-auth-token`) — readable even
 *     after the access token expires; `user.app_metadata.provider` names how
 *     this device last authenticated.
 *  2. Our own `fb-last-auth-method` marker, written on every SIGNED_IN
 *     (AuthContext). Supabase DELETES its key on sign-out and on a failed
 *     refresh, so the marker is what usually survives to the next visit.
 *
 * The server-truth complement (which methods the ACCOUNT has, regardless of
 * device) is fetchAuthMethodHint — only ever called after a failed sign-in or
 * a signup collision, never pre-auth.
 */
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export const AUTH_METHODS = ['google', 'facebook', 'azure', 'password'] as const;
export type AuthMethod = (typeof AUTH_METHODS)[number];

const MARKER_KEY = 'fb-last-auth-method';

export function isAuthMethod(v: unknown): v is AuthMethod {
  return typeof v === 'string' && (AUTH_METHODS as readonly string[]).includes(v);
}

/** Supabase provider name → our method name (null for unknowns). */
function toMethod(provider: unknown): AuthMethod | null {
  if (provider === 'email') return 'password';
  return isAuthMethod(provider) ? provider : null;
}

/** Human label, matching the button text it points at. */
export function methodLabel(m: AuthMethod): string {
  switch (m) {
    case 'google':
      return 'Google';
    case 'facebook':
      return 'Facebook';
    case 'azure':
      return 'Microsoft';
    case 'password':
      return 'email & password';
  }
}

type StorageLike = Pick<Storage, 'length' | 'key' | 'getItem' | 'setItem'>;

/**
 * Synchronous read of how this device last signed in, or null when it
 * genuinely doesn't know (new device, cleared storage). Safe everywhere:
 * swallows malformed JSON, missing storage, blocked access.
 */
export function readLastUsedMethod(
  storage: StorageLike | null = typeof window === 'undefined' ? null : window.localStorage,
): AuthMethod | null {
  if (!storage) return null;
  try {
    // Prefer the live/stale Supabase session — it's ground truth when present.
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key || !/^sb-.+-auth-token$/.test(key)) continue;
      const raw = storage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as {
          user?: { app_metadata?: { provider?: unknown } };
        };
        const m = toMethod(parsed?.user?.app_metadata?.provider);
        if (m) return m;
      } catch {
        // Unparseable session blob — fall through to the marker.
      }
    }
    const marker = storage.getItem(MARKER_KEY);
    return isAuthMethod(marker) ? marker : null;
  } catch {
    return null;
  }
}

/**
 * Record how a session authenticated — called from AuthContext on SIGNED_IN so
 * the hint survives sign-out and Supabase's own storage cleanup.
 */
export function rememberAuthMethod(
  session: Session | null,
  storage: StorageLike | null = typeof window === 'undefined' ? null : window.localStorage,
): void {
  if (!storage || !session?.user) return;
  const m = toMethod(session.user.app_metadata?.provider);
  if (!m) return;
  try {
    storage.setItem(MARKER_KEY, m);
  } catch {
    // Storage full/blocked — the hint is a convenience, never load-bearing.
  }
}

/**
 * Server-truth lookup: which methods can this EMAIL sign in with. Only call
 * after a failed sign-in or a signup collision (the endpoint is rate-limited
 * and a non-empty answer reveals the account exists — that trade is ratified
 * for exactly those two post-interaction moments). Null on any failure.
 */
export async function fetchAuthMethodHint(email: string): Promise<AuthMethod[] | null> {
  try {
    const { data, error } = await supabase.functions.invoke<{ methods?: unknown[] }>(
      'auth-method-hint',
      { body: { email } },
    );
    if (error || !Array.isArray(data?.methods)) return null;
    return data.methods.filter(isAuthMethod);
  } catch {
    return null;
  }
}
